import React, {FunctionComponent, useState} from "react";
import {PageTemplate} from "../../common/page-template/PageTemplate";
import {useTranslation} from "react-i18next";
import {useGetApiV1UserMeQuery, usePutApiV1UserMeMutation} from "../../../api/GovernanceToolApi";
import {ErrorMessage} from "../../common/error-message/ErrorMessage";
import {Loader} from "../../common/loader/Loader";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import {PasswordUtils} from "../../../book/PasswordUtils";
import {UserRoleUtils} from "../../../book/UserRoleUtils";
import {Box} from "@mui/material";
import {styled} from "@mui/material/styles";
import {
    ProcessStatusNotification,
    toProcessStatus
} from "../../common/process-status-notification/ProcessStatusNotification";

const ProfileBox = styled(Box)(() => ({
    '& .MuiBox-root': {
        marginBottom: '1em',
    },
    '& .MuiFormControl-root': {
        marginBottom: '1em'
    },
    '& .MuiAlert-root': {
        marginBottom: '1em'
    },
    '& .label': {
        fontWeight: 'bold'
    }
}));

export const Profile: FunctionComponent = () => {
    const {t} = useTranslation();
    const {data: userMe, isLoading: isMeLoading, error: meError} = useGetApiV1UserMeQuery(undefined);
    const [state, setState] = useState<ProfileState>({
        newPassword: '',
        repeatNewPassword: ''
    } as ProfileState);
    const [editMe, {
        data: editMeData,
        isLoading: isEditMeLoading,
        error: editMeError,
        reset: resetEditMe
    }] = usePutApiV1UserMeMutation();

    const username = userMe?.payload?.username;
    const role = userMe?.payload?.role;

    return <PageTemplate pageTitle={t('Profile')}>
        {Boolean(isMeLoading) && <Loader/>}

        {Boolean(meError) && <ErrorMessage
            message={t("An error occurred while loading your info, please contact the administrator or try again")}/>}

        {Boolean(editMeError) && <ErrorMessage
            message={t("An error occurred while updating your password, please contact the administrator or try again")}/>}

        {Boolean(userMe) && <ProfileBox>
            <Box>
                <span className="label">{t("Username")}:</span> {username}
            </Box>

            <Box>
                <span className="label">{t("Role")}:</span> {UserRoleUtils.ToRoleName(UserRoleUtils.ToRole(role))}
            </Box>

            <TextField value={state.newPassword}
                       onChange={e => {
                           setState(state => ({...state, newPassword: e.target.value}));
                       }}
                       disabled={isEditMeLoading}
                       variant="outlined"
                       label={t("Password")}
                       fullWidth
                       type="password"
                       helperText={t("passwordRole")}
                       inputProps={{
                           autoComplete: 'new-password',
                       }}/>

            <TextField value={state.repeatNewPassword}
                       onChange={e => {
                           setState(state => ({...state, repeatNewPassword: e.target.value}));
                       }}
                       disabled={isEditMeLoading}
                       variant="outlined"
                       label={t("Confirm password")}
                       fullWidth
                       type="password"
                       inputProps={{
                           autoComplete: 'repeat-password',
                       }}/>

            <Box sx={{overflow: 'hidden', marginBottom: '1em',}}>
                <Button onClick={() => editMe({updateMeRequest: {password: state.newPassword}})}
                        color="primary"
                        variant="contained"
                        sx={{float: 'right'}}
                        disabled={
                            isEditMeLoading
                            || state.newPassword !== state.repeatNewPassword
                            || (!!state.newPassword && !PasswordUtils.validate(state.newPassword || ''))
                        }>
                    {t("Update password")}
                </Button>
            </Box>

            {state.newPassword !== state.repeatNewPassword &&
                <Alert severity="warning">{t("Passwords do not match")}</Alert>}
            {(!!state.newPassword && !PasswordUtils.validate(state.newPassword || '')) &&
                <Alert severity="warning">{t("Password does not match rules")}</Alert>}
        </ProfileBox>}

        <ProcessStatusNotification
            status={toProcessStatus(isEditMeLoading, Boolean(editMeData?.payload), Boolean(editMeError))}
            onClose={() => resetEditMe()}
            onLoadingMessage={t("Updating password please wait…")}
            onSuccessMessage={t("Password updated successfully")}
            onErrorMessage={t("Password updating failed")}/>
    </PageTemplate>
}

interface ProfileState {
    newPassword: string;
    repeatNewPassword: string;
}